import { easybackClient } from './api'

export interface StudentEnrollment {
  id: number
  student_id: number
  course_id: number
  status: 'active' | 'completed' | 'paused' | 'cancelled'
  enrolled_at: string
  expires_at: string | null
  progress_percentage: number
  created_at: string
  updated_at: string
  deleted_at: string | null
}

interface CacheEntry<T> {
  data: T
  timestamp: number
}

class EnrollmentCache {
  private cache = new Map<string, CacheEntry<unknown>>()
  private defaultTTL = 5 * 60 * 1000

  set<T>(key: string, data: T, ttl: number = this.defaultTTL): void {
    this.cache.set(key, { data, timestamp: Date.now() + ttl })
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key)
    if (!entry) return null
    if (Date.now() > entry.timestamp) {
      this.cache.delete(key)
      return null
    }
    return entry.data as T
  }

  has(key: string): boolean {
    const entry = this.cache.get(key)
    if (!entry) return false
    if (Date.now() > entry.timestamp) {
      this.cache.delete(key)
      return false
    }
    return true
  }

  clear(): void {
    this.cache.clear()
  }

  delete(key: string): void {
    this.cache.delete(key)
  }

  clearStudent(studentId: number): void {
    const prefix = `student:${studentId}:`
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        this.cache.delete(key)
      }
    }
  }
}

const enrollmentCache = new EnrollmentCache()

export const enrollmentService = {
  getStudentEnrollments: async (studentId: number) => {
    const cacheKey = `student:${studentId}:enrollments`

    const cached = enrollmentCache.get<StudentEnrollment[]>(cacheKey)
    if (cached) return cached

    const result = await easybackClient.list<StudentEnrollment>('student_enrollments', {
      filter: { student_id: { eq: studentId } },
      sort: { enrolled_at: 'desc' }
    })

    const enrollments = result.data || []

    const uniqueEnrollments = enrollments.filter((enrollment, index, self) =>
      self.findIndex((e) => e.course_id === enrollment.course_id) === index
    )

    enrollmentCache.set(cacheKey, uniqueEnrollments)
    return uniqueEnrollments
  },

  getEnrollment: async (studentId: number, courseId: number) => {
    const cacheKey = `student:${studentId}:course:${courseId}:enrollment`

    const cached = enrollmentCache.get<StudentEnrollment | null>(cacheKey)
    if (cached !== null) return cached

    const result = await easybackClient.list<StudentEnrollment>('student_enrollments', {
      filter: {
        student_id: { eq: studentId },
        course_id: { eq: courseId }
      },
      limit: 1
    })

    const enrollment = result.data?.[0] || null
    enrollmentCache.set(cacheKey, enrollment)
    return enrollment
  },

  enrollStudent: async (studentId: number, courseId: number) => {
    const existing = await enrollmentService.getEnrollment(studentId, courseId)

    if (existing) {
      if (existing.status === 'cancelled' || existing.status === 'paused') {
        const updated = await easybackClient.update<StudentEnrollment>('student_enrollments', existing.id, {
          status: 'active',
        })
        enrollmentCache.clearStudent(studentId)
        return updated.data?.[0]
      }
      return existing
    }

    const enrollment = await easybackClient.insert<StudentEnrollment>('student_enrollments', {
      student_id: studentId,
      course_id: courseId,
      status: 'active',
      progress_percentage: 0,
    })

    enrollmentCache.clearStudent(studentId)
    return enrollment.data?.[0]
  },

  unenrollStudent: async (studentId: number, courseId: number) => {
    const enrollment = await enrollmentService.getEnrollment(studentId, courseId)

    if (enrollment) {
      await easybackClient.update<StudentEnrollment>('student_enrollments', enrollment.id, {
        status: 'cancelled',
      })
      enrollmentCache.clearStudent(studentId)
      return true
    }

    return false
  },

  updateProgress: async (enrollmentId: number, progressPercentage: number) => {
    const updated = await easybackClient.update<StudentEnrollment>('student_enrollments', enrollmentId, {
      progress_percentage: progressPercentage,
      status: progressPercentage >= 100 ? 'completed' : 'active',
    })

    const enrollment = updated.data?.[0]
    if (enrollment) {
      enrollmentCache.clearStudent(enrollment.student_id)
    }

    return updated.data?.[0]
  },

  getActiveEnrollments: async (studentId: number) => {
    const cacheKey = `student:${studentId}:active_enrollments`

    const cached = enrollmentCache.get<StudentEnrollment[]>(cacheKey)
    if (cached) return cached

    const result = await easybackClient.list<StudentEnrollment>('student_enrollments', {
      filter: {
        student_id: { eq: studentId },
        status: { eq: 'active' }
      },
      sort: { enrolled_at: 'desc' }
    })

    const enrollments = result.data || []
    enrollmentCache.set(cacheKey, enrollments)
    return enrollments
  },

  getCompletedEnrollments: async (studentId: number) => {
    const cacheKey = `student:${studentId}:completed_enrollments`

    const cached = enrollmentCache.get<StudentEnrollment[]>(cacheKey)
    if (cached) return cached

    const result = await easybackClient.list<StudentEnrollment>('student_enrollments', {
      filter: {
        student_id: { eq: studentId },
        status: { eq: 'completed' }
      },
      sort: { enrolled_at: 'desc' }
    })

    const enrollments = result.data || []
    enrollmentCache.set(cacheKey, enrollments)
    return enrollments
  },

  hasAccess: async (studentId: number, courseId: number): Promise<boolean> => {
    const cacheKey = `student:${studentId}:course:${courseId}:access`

    const cached = enrollmentCache.get<boolean>(cacheKey)
    if (cached !== null) return cached

    const enrollment = await enrollmentService.getEnrollment(studentId, courseId)

    if (!enrollment) {
      enrollmentCache.set(cacheKey, false)
      return false
    }

    if (enrollment.status !== 'active' && enrollment.status !== 'completed') {
      enrollmentCache.set(cacheKey, false)
      return false
    }

    if (enrollment.expires_at) {
      const expiryDate = new Date(enrollment.expires_at)
      if (expiryDate < new Date()) {
        enrollmentCache.set(cacheKey, false)
        return false
      }
    }

    enrollmentCache.set(cacheKey, true)
    return true
  },

  getUserEnrollment: async (userId: number, courseId: number) => {
    const result = await easybackClient.list<StudentEnrollment>('student_enrollments', {
      filter: {
        student_id: { eq: userId },
        course_id: { eq: courseId }
      },
      limit: 1
    })
    return result.data?.[0] || null
  },

  enrollUser: async (userId: number, courseId: number) => {
    const existing = await enrollmentService.getUserEnrollment(userId, courseId)

    if (existing) {
      if (existing.status === 'cancelled' || existing.status === 'paused') {
        const updated = await easybackClient.update<StudentEnrollment>('student_enrollments', existing.id, {
          status: 'active',
        })
        enrollmentCache.clearStudent(userId)
        return updated.data?.[0]
      }
      return existing
    }

    const enrollment = await easybackClient.insert<StudentEnrollment>('student_enrollments', {
      student_id: userId,
      course_id: courseId,
      status: 'active',
      progress_percentage: 0,
    })

    enrollmentCache.clearStudent(userId)
    return enrollment.data?.[0]
  },

  getEnrollmentById: async (enrollmentId: number) => {
    const result = await easybackClient.get<StudentEnrollment>('student_enrollments', enrollmentId)
    return result.data?.[0] || null
  },

  clearCache: (studentId?: number): void => {
    if (studentId) {
      enrollmentCache.clearStudent(studentId)
    } else {
      enrollmentCache.clear()
    }
  },
}
